import { CalendarDays } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  formatDisplayDate,
  formatIsoDate,
  MONTH_LABELS,
  parseIsoDate,
  startOfDay,
} from "@/lib/calendarDates";
import { useDismissable } from "@/lib/useDismissable";
import { CalendarGrid, type DayVariant } from "./CalendarGrid";

export function DateRangePicker({
  from,
  to,
  onChange,
  placeholder = "Filter by date",
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const ref = useDismissable<HTMLDivElement>(open, (reason) => {
    setOpen(false);

    if (reason === "escape") {
      triggerRef.current?.focus();
    }
  });

  const today = useMemo(() => startOfDay(new Date()), []);
  const fromDate = useMemo(() => parseIsoDate(from), [from]);
  const [activeDate, setActiveDate] = useState(() => fromDate ?? today);

  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect
      setActiveDate(fromDate ?? today);
    }
  }, [open, fromDate, today]);

  function selectDay(day: Date) {
    const iso = formatIsoDate(day);

    if (!from || (from && to)) {
      onChange(iso, "");
      return;
    }

    if (iso < from) {
      onChange(iso, "");
      return;
    }

    onChange(from, iso);

    setOpen(false);
  }

  function variantOf(iso: string): DayVariant {
    if (iso === from || iso === to) {
      return "selected";
    }

    return from && to && iso > from && iso < to ? "inRange" : "none";
  }

  function triggerLabel(): string {
    if (!fromDate) {
      return placeholder;
    }

    if (!to) {
      return formatDisplayDate(fromDate);
    }

    const toDate = parseIsoDate(to);

    return toDate
      ? `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`
      : formatDisplayDate(fromDate);
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-10 items-center gap-2 rounded-lg border border-muted bg-surface px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
      >
        <CalendarDays className="h-4 w-4 text-foreground" strokeWidth={2} />
        <span>{triggerLabel()}</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose a date range"
          className="absolute right-0 z-10 mt-1 w-64 rounded-lg border border-muted bg-surface p-3 shadow-2xl"
        >
          {from && !to && (
            <p className="mb-2 text-xs text-foreground/70">
              Pick an end date...
            </p>
          )}
          <CalendarGrid
            activeDate={activeDate}
            onActiveDateChange={setActiveDate}
            onSelect={selectDay}
            variantOf={variantOf}
            maxDate={today}
            label={`${MONTH_LABELS[activeDate.getMonth()]} ${activeDate.getFullYear()}`}
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                onChange("", "");
                setOpen(false);
              }}
              className="mt-2 text-sm text-foreground hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
